import React, { useState, useContext } from "react";
import {
  Box,
  Button,
  Container,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import ErrorAlert from "../common/ErrorAlert";
import { ExecutionsTable } from "./table/executionsTable";
import { useRedriveData } from "./hooks/useRedriveData";
import { StageSelector } from "./components/StageSelector";
import { FileUploader } from "./components/FileUploader";
import { useExecutePaymentRedrive } from "./hooks/useExecutePaymentRedrive";
import { usePaymentFileValidation } from "./hooks/usePaymentFileValidation";
import { StageContext } from "../../contexes/OrderIdContext";
import { Stage } from "../../lusca-search-api/generated-src";

interface Props {
  setStage: React.Dispatch<React.SetStateAction<Stage>>;
}

export default function PaymentRedriveContent({ setStage }: Props) {
  const stage = useContext(StageContext);
  const [loading, setLoading] = useState(false);
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(
    null
  );

  const {
    pastRedrives,
    loading: loadingRedrives,
    error: fetchError,
  } = useRedriveData(stage);
  const {
    file,
    error: fileError,
    redriveOK,
    handleFileChange,
    validateRedrive,
    setError,
    removeDuplicates,
  } = usePaymentFileValidation();
  const { executeRedrive: executeRedriveHook } = useExecutePaymentRedrive();

  const error = [fetchError, fileError].filter(Boolean).join("; ") || null;

  const executeRedrive = async () => {
    if (file) {
      setLoading(true);
      await executeRedriveHook(file, stage, setSelectedExecutionId, setError);
      setLoading(false);
    }
  };

  return (
    <SpaceBetween size="xl">
      <SpaceBetween size="l">
        {error && <ErrorAlert content={error} header="Payment Redrive Error" />}

        <Container
          header={<Box variant="h2">Payment Document Redrive Tool</Box>}
        >
          <SpaceBetween size="m">
            <Box variant="p">
              This tool allows you to redrive payment documents. Upload a CSV
              file with payment IDs (one per line), or redrive a single payment
              from that payment&#39;s document view.
            </Box>
            <StageSelector stage={stage} setStage={setStage} />
          </SpaceBetween>
        </Container>

        <Container>
          <SpaceBetween size="m">
            <FileUploader
              file={file}
              handleFileChange={handleFileChange}
              validateRedrive={validateRedrive}
              redriveOK={redriveOK}
              removeDuplicates={removeDuplicates}
              loading={loading}
            />
            {file && redriveOK && (
              <Button
                onClick={executeRedrive}
                variant="primary"
                disabled={loading}
                loading={loading}
              >
                Execute Redrive
              </Button>
            )}
          </SpaceBetween>
        </Container>

        <ExecutionsTable items={pastRedrives} loading={loadingRedrives} />
      </SpaceBetween>
    </SpaceBetween>
  );
}
